import React, { useState, useEffect } from 'react';
import { buildApiUrl } from '../config';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';

const MyClubs = ({ teacherEmail, onBack }) => {
  const [clubs, setClubs] = useState([]);
  const [selectedClub, setSelectedClub] = useState(null);
  const [students, setStudents] = useState([]);
  const [attendanceDate, setAttendanceDate] = useState(new Date().toISOString().split('T')[0]);
  const [attendanceRecords, setAttendanceRecords] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [sessionNotes, setSessionNotes] = useState('');

  useEffect(() => {
    loadTeacherClubs();
  }, [teacherEmail]);

  const loadTeacherClubs = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching clubs for teacher:', teacherEmail);
      console.log('🌐 API URL:', buildApiUrl(`/api/teacher/${teacherEmail}/clubs`));
      
      const response = await fetch(buildApiUrl(`/api/teacher/${teacherEmail}/clubs`));
      console.log('📡 Response status:', response.status);
      
      const data = await response.json();
      console.log('📦 Raw API response:', data);
      
      if (data.success) {
        setClubs(data.clubs || []);
        console.log('📚 Loaded teacher clubs:', data.clubs);
        console.log('📊 Number of clubs found:', data.clubs ? data.clubs.length : 0);
      } else {
        console.error('❌ Failed to load clubs:', data.error);
        // Set empty clubs array when API returns success=false
        setClubs([]);
      }
    } catch (error) {
      console.error('❌ Network/Parse error loading clubs:', error);
      setClubs([]);
    } finally {
      setLoading(false);
    }
  };

  const loadClubStudents = async (clubId) => {
    try {
      const response = await fetch(buildApiUrl(`/api/clubs/${clubId}/students`));
      const data = await response.json();
      
      if (data.success) {
        setStudents(data.students);
        setSelectedClub(data.club);
        
        // Initialize attendance records for all students as 'present'
        const initialAttendance = {};
        data.students.forEach(student => {
          initialAttendance[student.student_school_id] = {
            status: 'present',
            notes: ''
          };
        });
        setAttendanceRecords(initialAttendance);
        
        console.log('👥 Loaded club students:', data.students);
      } else {
        console.error('❌ Failed to load students:', data.error);
      }
    } catch (error) {
      console.error('❌ Error loading students:', error);
    }
  };

  const loadAttendanceHistory = async (clubId) => {
    try {
      const response = await fetch(buildApiUrl(`/api/clubs/${clubId}/attendance?teacher_email=${teacherEmail}`));
      const data = await response.json();
      
      if (data.success) {
        setAttendanceHistory(data.attendance_records);
        console.log('📊 Loaded attendance history:', data.attendance_records);
      } else {
        console.error('❌ Failed to load attendance history:', data.error);
      }
    } catch (error) {
      console.error('❌ Error loading attendance history:', error);
    }
  };

  const handleAttendanceChange = (studentId, field, value) => {
    setAttendanceRecords(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        [field]: value
      }
    }));
  };

  const submitAttendance = async () => {
    if (!selectedClub || Object.keys(attendanceRecords).length === 0) {
      alert('Please select students and mark attendance');
      return;
    }

    try {
      setSubmitting(true);
      
      // Convert attendance records to the format expected by the API
      const studentRecords = Object.entries(attendanceRecords).map(([studentId, record]) => {
        // Find student name from the students array
        const student = students.find(s => s.student_school_id === studentId);
        const studentName = student ? student.student_name : studentId;
        
        return {
          student_school_id: studentId,
          student_name: studentName,
          status: record.status,
          notes: record.notes || ''
        };
      });

      const attendanceData = {
        club_id: selectedClub.id,
        session_date: attendanceDate,
        student_records: studentRecords,
        teacher_email: teacherEmail,
        notes: sessionNotes
      };

      console.log('📝 Submitting attendance:', attendanceData);

      const response = await fetch(buildApiUrl(`/api/clubs/${selectedClub.id}/attendance`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(attendanceData)
      });

      const data = await response.json();
      
      if (data.success) {
        alert('✅ Attendance recorded successfully!');
        setSessionNotes('');
        
        // Refresh attendance history if showing
        if (showHistory) {
          loadAttendanceHistory(selectedClub.id);
        }
      } else {
        alert(`❌ Failed to record attendance: ${data.error}`);
      }
    } catch (error) {
      console.error('❌ Error submitting attendance:', error);
      alert('❌ Error submitting attendance. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    
    // Title
    doc.setFontSize(18);
    doc.text(`${selectedClub.name} - Attendance History`, 14, 22);
    
    // Club Details
    doc.setFontSize(10);
    doc.text(`${selectedClub.day_of_week} • ${selectedClub.start_time} - ${selectedClub.end_time}`, 14, 30);
    if (selectedClub.location) {
      doc.text(`Location: ${selectedClub.location}`, 14, 36);
    }
    
    // Prepare table data
    const tableData = [];
    attendanceHistory.forEach((record) => {
      record.student_records.forEach((studentRecord) => {
        tableData.push([
          new Date(record.session_date).toLocaleDateString('en-GB'),
          studentRecord.student_name || studentRecord.student_school_id,
          studentRecord.student_school_id,
          studentRecord.status === 'present' ? 'Present' : 'Absent',
          studentRecord.notes || '-',
          new Date(new Date(record.recorded_at).getTime() + 3 * 60 * 60 * 1000).toLocaleDateString('en-GB')
        ]);
      });
    });
    
    // Add table
    doc.autoTable({
      startY: selectedClub.location ? 42 : 36,
      head: [['Session Date', 'Student Name', 'Student ID', 'Status', 'Notes', 'Recorded Date']],
      body: tableData,
      theme: 'grid',
      headStyles: { fillColor: [34, 197, 94] },
      styles: { fontSize: 8 },
      columnStyles: {
        3: { cellWidth: 20 },
        4: { cellWidth: 35 }
      }
    });
    
    // Save
    doc.save(`${selectedClub.name}_Attendance_History.pdf`);
  };

  const exportToExcel = () => {
    // Prepare data for Excel
    const excelData = [];
    
    attendanceHistory.forEach((record) => {
      record.student_records.forEach((studentRecord) => {
        excelData.push({
          'Session Date': new Date(record.session_date).toLocaleDateString('en-GB'),
          'Student Name': studentRecord.student_name || studentRecord.student_school_id,
          'Student ID': studentRecord.student_school_id,
          'Status': studentRecord.status === 'present' ? 'Present' : 'Absent',
          'Notes': studentRecord.notes || '-',
          'Session Notes': record.notes || '-',
          'Recorded Date': new Date(new Date(record.recorded_at).getTime() + 3 * 60 * 60 * 1000).toLocaleDateString('en-GB'),
          'Recorded Time': new Date(new Date(record.recorded_at).getTime() + 3 * 60 * 60 * 1000).toLocaleTimeString('en-GB')
        });
      });
    });
    
    // Create workbook
    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Attendance History');
    
    // Save file
    XLSX.writeFile(wb, `${selectedClub.name}_Attendance_History.xlsx`);
  };

  const handleClubSelect = (club) => {
    loadClubStudents(club.id);
    if (showHistory) {
      loadAttendanceHistory(club.id);
    }
  };

  const toggleHistory = () => {
    if (!showHistory && selectedClub) {
      loadAttendanceHistory(selectedClub.id);
    }
    setShowHistory(!showHistory);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your clubs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center space-x-4 mb-4">
            {onBack && (
              <button
                onClick={onBack}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
                title="Back"
              >
                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
              <span className="text-white text-2xl">🏃‍♂️</span>
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">My Clubs</h1>
              <p className="text-gray-600">Manage your assigned after-school clubs and take attendance</p>
          </div>
        </div>

        {clubs.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">🏃‍♂️</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Clubs Assigned</h3>
            <p className="text-gray-600">You don't have any clubs assigned to you at the moment.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {clubs.map((club) => (
              <div 
                key={club.id} 
                className={`border rounded-lg p-4 cursor-pointer transition-all hover:shadow-md ${
                  selectedClub?.id === club.id 
                    ? 'border-green-500 bg-green-50 shadow-md' 
                    : 'border-gray-200 hover:border-green-300'
                }`}
                onClick={() => handleClubSelect(club)}
              >
                <div className="flex justify-between items-start mb-3">
                  <h3 className="font-semibold text-gray-900">{club.name}</h3>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    club.total_students >= club.capacity * 0.9 
                      ? 'bg-red-100 text-red-800' 
                      : club.total_students >= club.capacity * 0.7
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {club.total_students}/{club.capacity} students
                  </span>
                </div>

                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <span>📅</span>
                    <span>{club.day_of_week}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span>🕐</span>
                    <span>{club.start_time} - {club.end_time}</span>
                  </div>
                  {club.location && (
                    <div className="flex items-center gap-2">
                      <span>📍</span>
                      <span>{club.location}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <span>📋</span>
                    <span>{club.period_name}</span>
                  </div>
                </div>

                {club.description && (
                  <p className="text-xs text-gray-500 mt-3 bg-gray-50 p-2 rounded">
                    {club.description}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Club Details and Attendance */}
      {selectedClub && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSelectedClub(null)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Back to clubs list"
              >
                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div>
                <h2 className="text-2xl font-semibold text-gray-900">{selectedClub.name}</h2>
                <p className="text-gray-600">
                  {selectedClub.day_of_week} • {selectedClub.start_time} - {selectedClub.end_time}
                  {selectedClub.location && ` • ${selectedClub.location}`}
                </p>
              </div>
            </div>
            <button
              onClick={toggleHistory}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {showHistory ? '📝 Take Attendance' : '📊 View History'}
            </button>
          </div>

          {showHistory ? (
            /* Attendance History View - Table Format */
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">Attendance History</h3>
                {attendanceHistory.length > 0 && (
                  <div className="flex gap-2">
                    <button
                      onClick={exportToPDF}
                      className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Export PDF
                    </button>
                    <button
                      onClick={exportToExcel}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Export Excel
                    </button>
                  </div>
                )}
              </div>
              
              {attendanceHistory.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-600">No attendance records yet for this club.</p>
                </div>
              ) : (
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gradient-to-r from-green-500 to-emerald-600">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                          Session Date
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                          Student Name
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                          Student ID
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                          Notes
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                          Recorded
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {attendanceHistory.flatMap((record) => 
                        record.student_records.map((studentRecord, index) => (
                          <tr key={`${record.id}-${studentRecord.student_school_id}`} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {new Date(record.session_date).toLocaleDateString('en-GB')}
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                              {studentRecord.student_name || studentRecord.student_school_id}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {studentRecord.student_school_id}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                studentRecord.status === 'present' 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {studentRecord.status === 'present' ? '✅ Present' : '❌ Absent'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {studentRecord.notes || '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {new Date(new Date(record.recorded_at).getTime() + 3 * 60 * 60 * 1000).toLocaleDateString('en-GB')}
                              <br />
                              <span className="text-xs text-gray-500">
                                {new Date(new Date(record.recorded_at).getTime() + 3 * 60 * 60 * 1000).toLocaleTimeString('en-GB')}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                  
                  {/* Summary Stats */}
                  <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">
                        Total Records: <span className="font-semibold text-gray-900">
                          {attendanceHistory.reduce((sum, record) => sum + record.student_records.length, 0)}
                        </span>
                      </span>
                      <span className="text-gray-600">
                        Sessions: <span className="font-semibold text-gray-900">
                          {attendanceHistory.length}
                        </span>
                      </span>
                      <span className="text-green-600">
                        Present: <span className="font-semibold">
                          {attendanceHistory.reduce((sum, record) => 
                            sum + record.student_records.filter(s => s.status === 'present').length, 0
                          )}
                        </span>
                      </span>
                      <span className="text-red-600">
                        Absent: <span className="font-semibold">
                          {attendanceHistory.reduce((sum, record) => 
                            sum + record.student_records.filter(s => s.status === 'absent').length, 0
                          )}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Take Attendance View - Table Format */
            <div className="space-y-6">
              {/* Date Selection */}
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Session Date
                  </label>
                  <input
                    type="date"
                    value={attendanceDate}
                    onChange={(e) => setAttendanceDate(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
                
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Session Notes (Optional)
                  </label>
                  <input
                    type="text"
                    value={sessionNotes}
                    onChange={(e) => setSessionNotes(e.target.value)}
                    placeholder="Any notes about this session..."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Students List */}
              {students.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-600">No students registered for this club.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Club Register ({students.length} students)
                    </h3>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          const newRecords = { ...attendanceRecords };
                          students.forEach(student => {
                            newRecords[student.student_school_id] = { ...newRecords[student.student_school_id], status: 'present' };
                          });
                          setAttendanceRecords(newRecords);
                        }}
                        className="px-3 py-1.5 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200 font-medium transition-colors"
                      >
                        ✅ Mark All Present
                      </button>
                      <button
                        onClick={() => {
                          const newRecords = { ...attendanceRecords };
                          students.forEach(student => {
                            newRecords[student.student_school_id] = { ...newRecords[student.student_school_id], status: 'absent' };
                          });
                          setAttendanceRecords(newRecords);
                        }}
                        className="px-3 py-1.5 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200 font-medium transition-colors"
                      >
                        ❌ Mark All Absent
                      </button>
                    </div>
                  </div>

                  <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gradient-to-r from-green-500 to-emerald-600">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                            Student Name
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                            Student ID
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">
                            Attendance Status
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                            Notes
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {students.map((student) => (
                          <tr key={student.student_school_id} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <div>
                                <div className="text-sm font-medium text-gray-900">{student.student_name}</div>
                                {student.special_requirements && (
                                  <div className="text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded mt-1 inline-block">
                                    ⚠️ {student.special_requirements}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {student.student_school_id}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex justify-center gap-2">
                                <button
                                  onClick={() => handleAttendanceChange(student.student_school_id, 'status', 'present')}
                                  className={`px-4 py-2 text-sm rounded-lg font-medium transition-all ${
                                    attendanceRecords[student.student_school_id]?.status === 'present'
                                      ? 'bg-green-600 text-white shadow-md'
                                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                                  }`}
                                >
                                  ✅ Present
                                </button>
                                <button
                                  onClick={() => handleAttendanceChange(student.student_school_id, 'status', 'absent')}
                                  className={`px-4 py-2 text-sm rounded-lg font-medium transition-all ${
                                    attendanceRecords[student.student_school_id]?.status === 'absent'
                                      ? 'bg-red-600 text-white shadow-md'
                                      : 'bg-red-100 text-red-700 hover:bg-red-200'
                                  }`}
                                >
                                  ❌ Absent
                                </button>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <input
                                type="text"
                                value={attendanceRecords[student.student_school_id]?.notes || ''}
                                onChange={(e) => handleAttendanceChange(student.student_school_id, 'notes', e.target.value)}
                                placeholder="Optional notes..."
                                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    
                    {/* Summary Footer */}
                    <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">
                          Total Students: <span className="font-semibold text-gray-900">{students.length}</span>
                        </span>
                        <span className="text-green-600">
                          Marked Present: <span className="font-semibold">
                            {Object.values(attendanceRecords).filter(r => r.status === 'present').length}
                          </span>
                        </span>
                        <span className="text-red-600">
                          Marked Absent: <span className="font-semibold">
                            {Object.values(attendanceRecords).filter(r => r.status === 'absent').length}
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="flex justify-end pt-4">
                    <button
                      onClick={submitAttendance}
                      disabled={submitting}
                      className="px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2 shadow-md hover:shadow-lg font-medium"
                    >
                      {submitting ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Recording...
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Record Attendance
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
};

export default MyClubs;
